/**
 * Cookie Consent Manager
 * Manages user cookie preferences and persistence to localStorage
 */

const STORAGE_KEY = 'cookie_consent_preferences'

/**
 * Default preferences structure
 */
const defaultPreferences = {
  essential: true,      // Always true, cannot be disabled
  analytics: false,     // Google Analytics consent
  consentGiven: false,  // Whether user has made a choice
  timestamp: null       // ISO timestamp of consent
}

/**
 * Get consent preferences from localStorage
 * @returns {Object|null} Cookie preferences or null if not set
 */
export const getConsentPreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    
    const parsed = JSON.parse(stored)
    
    // Validate the structure
    if (typeof parsed !== 'object' || parsed === null) {
      clearConsentPreferences()
      return null
    }
    
    // Ensure essential is always true
    if (parsed.essential !== true) {
      parsed.essential = true
    }
    
    return parsed
  } catch (error) {
    // Handle localStorage errors (private browsing, corrupted data)
    console.warn('Cookie consent: Error reading preferences', error)
    return null
  }
}

/**
 * Set consent preferences to localStorage
 * @param {Object} preferences - Partial preferences to merge
 */
export const setConsentPreferences = (preferences) => {
  try {
    const current = getConsentPreferences() || { ...defaultPreferences }
    
    const updated = {
      ...current,
      ...preferences,
      essential: true, // Always enforce essential cookies
      consentGiven: true,
      timestamp: new Date().toISOString()
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    return updated
  } catch (error) {
    // Handle localStorage errors (private browsing, quota exceeded)
    console.warn('Cookie consent: Error saving preferences', error)
    return null
  }
}

/**
 * Check if user has given consent (made a choice)
 * @returns {boolean}
 */
export const hasConsentBeenGiven = () => {
  const prefs = getConsentPreferences()
  return prefs?.consentGiven === true
}

/**
 * Check if user has consented to analytics cookies
 * @returns {boolean}
 */
export const hasAnalyticsConsent = () => {
  const prefs = getConsentPreferences()
  return prefs?.consentGiven === true && prefs?.analytics === true
}

/**
 * Accept all cookies (essential + analytics)
 * @returns {Object} Updated preferences
 */
export const acceptAllCookies = () => {
  return setConsentPreferences({
    essential: true,
    analytics: true
  })
}

/**
 * Reject non-essential cookies (only essential enabled)
 * @returns {Object} Updated preferences
 */
export const rejectNonEssentialCookies = () => {
  return setConsentPreferences({
    essential: true,
    analytics: false
  })
}

/**
 * Clear all consent preferences from localStorage
 */
export const clearConsentPreferences = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.warn('Cookie consent: Error clearing preferences', error)
  }
}

/**
 * Update specific cookie category
 * @param {string} category - Cookie category (analytics)
 * @param {boolean} enabled - Whether to enable the category
 * @returns {Object} Updated preferences
 */
export const updateCookieCategory = (category, enabled) => {
  if (category === 'essential') {
    // Essential cookies cannot be disabled
    return getConsentPreferences()
  }
  
  return setConsentPreferences({
    [category]: enabled
  })
}
